import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Calendar, Trash2, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../utils/api";

const AppointmentsPage = () => {
  const { token } = useAuth();
  const location = useLocation();
  const [appointments, setAppointments] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(location.search.includes("new=true"));
  const [formData, setFormData] = useState({ patient_id: "", date: "", time: "", notes: "" });

  const fetchData = async () => {
    try {
      const [appointmentsRes, patientsRes] = await Promise.all([
        api.get("/appointments", token),
        api.get("/patients", token)
      ]);
      setAppointments(appointmentsRes.data);
      setPatients(patientsRes.data);
    } catch (error) {
      toast.error("خطأ في تحميل المواعيد");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/appointments", formData, token);
      toast.success("تم حجز الموعد بنجاح");
      setShowModal(false);
      setFormData({ patient_id: "", date: "", time: "", notes: "" });
      fetchData();
    } catch (error) {
      toast.error("خطأ في حجز الموعد");
    }
  };

  const deleteAppointment = async (id) => {
    if (window.confirm("هل أنت متأكد من حذف هذا الموعد؟")) {
      try {
        await api.delete(`/appointments/${id}`, token);
        toast.success("تم حذف الموعد");
        fetchData();
      } catch (error) {
        toast.error("خطأ في حذف الموعد");
      }
    }
  };

  return (
    <div className="animate-fade-in" data-testid="appointments-page">
      <div className="page-header flex justify-between items-start">
        <div>
          <h1 className="page-title">المواعيد</h1>
          <p className="page-subtitle">إدارة مواعيد المرضى</p>
        </div>
        <button onClick={() => setShowModal(true)} className="btn btn-primary" data-testid="add-appointment-btn">
          <Plus className="w-5 h-5" />
          حجز موعد
        </button>
      </div>

      {loading ? (
        <div className="text-center py-8 text-gray-500">جاري التحميل...</div>
      ) : appointments.length === 0 ? (
        <div className="empty-state data-table">
          <Calendar className="empty-state-icon" />
          <h3>لا توجد مواعيد</h3>
          <p>ابدأ بحجز موعد جديد</p>
        </div>
      ) : (
        <div className="data-table">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="text-right py-3 px-4 font-medium text-gray-500">المريض</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500">التاريخ</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500">الوقت</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500">الحالة</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500">إجراءات</th>
                </tr>
              </thead>
              <tbody>
                {appointments.map((apt) => (
                  <tr key={apt.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`appointment-row-${apt.id}`}>
                    <td className="py-3 px-4 font-medium">{apt.patient_name}</td>
                    <td className="py-3 px-4">{apt.date}</td>
                    <td className="py-3 px-4">{apt.time}</td>
                    <td className="py-3 px-4">
                      <span className={`px-3 py-1 rounded-full text-sm ${
                        apt.status === "scheduled" ? "badge-info" :
                        apt.status === "completed" ? "badge-success" : "badge-neutral"
                      }`}>
                        {apt.status === "scheduled" ? "محجوز" : apt.status === "completed" ? "مكتمل" : "ملغي"}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <button
                        onClick={() => deleteAppointment(apt.id)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                        data-testid={`delete-appointment-${apt.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">حجز موعد جديد</h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">المريض *</label>
                  <select
                    className="form-input"
                    value={formData.patient_id}
                    onChange={(e) => setFormData({ ...formData, patient_id: e.target.value })}
                    required
                    data-testid="appointment-patient-select"
                  >
                    <option value="">اختر مريض</option>
                    {patients.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="form-group">
                    <label className="form-label">التاريخ *</label>
                    <input
                      type="date"
                      className="form-input"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      required
                      data-testid="appointment-date-input"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">الوقت *</label>
                    <input
                      type="time"
                      className="form-input"
                      value={formData.time}
                      onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                      required
                      data-testid="appointment-time-input"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">ملاحظات</label>
                  <textarea
                    className="form-input"
                    rows={2}
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    data-testid="appointment-notes-input"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="submit" className="btn btn-primary" data-testid="save-appointment-btn">
                  حجز الموعد
                </button>
                <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppointmentsPage;
